import { create } from 'zustand';
import { apiGet, apiPost } from '../lib/api-client';
import type { HandRecord } from '@range-reader/shared';
import type { FilterSettings } from '@range-reader/shared';
import type { EliminationLogEntry, RangeSnapshot, MatrixCellStatus } from '@range-reader/shared';
import { showErrorToast } from './toastStore';
import {
  MATRIX_CELLS,
  computeAllComboCounts,
  computeScore,
  detectIsolationStreet,
  holeCardsToCategory,
  parseCards,
  type Street,
  type ScoreBreakdown,
  type HandCategory,
  type Card,
} from '@range-reader/poker-core';

export type SessionPhase =
  | 'IDLE'
  | 'LOADING_HAND'
  | 'AWAITING_RANGE_UPDATE'
  | 'REPLAYING'
  | 'SHOWDOWN';

function makeInitialMatrix(defaultState: 'full' | 'blank'): MatrixCellStatus[] {
  const status: MatrixCellStatus = defaultState === 'full' ? 'HIGHLIGHTED' : 'UNHIGHLIGHTED';
  return Array(169).fill(status);
}

function parseStreet(s: string): Street {
  const map: Record<string, Street> = {
    preflop: 'preflop', flop: 'flop', turn: 'turn', river: 'river',
  };
  return map[s.toLowerCase()] ?? 'preflop';
}

function getExposedCards(hand: HandRecord, street: Street): Card[] {
  const community = hand.communityCards?.split(' ').filter(Boolean) ?? [];
  let cardStrs: string[] = [];
  if (street === 'preflop') cardStrs = [];
  else if (street === 'flop') cardStrs = community.slice(0, 3);
  else if (street === 'turn') cardStrs = community.slice(0, 4);
  else cardStrs = community;
  return parseCards(cardStrs.join(' '));
}

interface SessionState {
  phase: SessionPhase;
  hand: HandRecord | null;
  actionIndex: number;
  currentStreet: Street;
  filtersUsed: FilterSettings | null;

  matrixState: MatrixCellStatus[];
  comboCounts: number[];
  focusedCell: number;
  eliminationLog: EliminationLogEntry[];
  snapshots: RangeSnapshot[];
  matrixDefault: 'full' | 'blank';
  _matrixHistory: MatrixCellStatus[][];

  scoreBreakdown: ScoreBreakdown | null;
  sessionId: string | null;

  resetSession: () => void;
  startSession: (filters: FilterSettings, matrixDefault?: 'full' | 'blank') => Promise<void>;
  nextAction: () => void;
  nextStreet: () => void;
  skipToShowdown: () => void;
  replayHand: () => void;
  nextHand: () => Promise<void>;

  continueAfterRangeUpdate: () => void;
  toggleCell: (idx: number) => void;
  selectAll: () => void;
  clearAll: () => void;
  undoLastToggle: () => void;
  setFocusedCell: (idx: number) => void;

  _saveSnapshot: (triggerLabel: string) => void;
  _advanceToShowdown: (skipped?: boolean) => void;
  _lockRemovedCells: () => void;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  phase: 'IDLE',
  hand: null,
  actionIndex: -1,
  currentStreet: 'preflop',
  filtersUsed: null,

  matrixState: Array(169).fill('HIGHLIGHTED' as MatrixCellStatus),
  comboCounts: Array(169).fill(0),
  focusedCell: 0,
  eliminationLog: [],
  snapshots: [],
  matrixDefault: 'full',
  _matrixHistory: [],

  scoreBreakdown: null,
  sessionId: null,

  resetSession: () => {
    set({
      phase: 'IDLE',
      hand: null,
      actionIndex: -1,
      currentStreet: 'preflop',
      matrixState: Array(169).fill('HIGHLIGHTED' as MatrixCellStatus),
      comboCounts: Array(169).fill(0),
      focusedCell: 0,
      eliminationLog: [],
      snapshots: [],
      _matrixHistory: [],
      scoreBreakdown: null,
      sessionId: null,
    });
  },

  startSession: async (filters, matrixDefault = 'full') => {
    set({ phase: 'LOADING_HAND', filtersUsed: filters, matrixDefault });
    try {
      const res = await apiGet<{ hand: HandRecord }>('/api/hands/random', {
        filters: JSON.stringify(filters),
      });
      if (!res?.hand) {
        console.error('No hand returned:', res);
        set({ phase: 'IDLE' });
        return;
      }
      const hand = res.hand;
      const matrix = makeInitialMatrix(matrixDefault);
      const comboCounts = computeAllComboCounts([]);

      set({
        phase: 'AWAITING_RANGE_UPDATE',
        hand,
        actionIndex: -1,
        currentStreet: 'preflop',
        matrixState: matrix,
        comboCounts,
        eliminationLog: [],
        snapshots: [],
        _matrixHistory: [],
        scoreBreakdown: null,
        sessionId: null,
        focusedCell: 0,
      });

      get()._saveSnapshot('Session start');
    } catch (e) {
      console.error('Failed to start session:', e);
      showErrorToast('Failed to load hand. Please try again.');
      set({ phase: 'IDLE' });
    }
  },

  nextAction: () => {
    const { hand, actionIndex, phase, currentStreet } = get();
    if (!hand || phase === 'SHOWDOWN') return;

    const actions = hand.actions ?? [];
    const targetName = hand.winner.name;
    const RANGE_UPDATE_ACTIONS = ['bet', 'raise', 'check', 'allin', 'call', 'fold'];

    // Scan forward from current position to find the next target-player action
    // or a street change (whichever comes first)
    let targetIdx = -1;
    let streetChangeIdx = -1;
    let newStreet = currentStreet;

    for (let i = actionIndex + 1; i < actions.length; i++) {
      const act = actions[i];
      const actStreet = act.street ? parseStreet(act.street) : currentStreet;

      // If we cross into a new street, stop here — always pause on street transitions
      if (actStreet !== currentStreet && streetChangeIdx === -1) {
        streetChangeIdx = i;
        newStreet = actStreet;
        break;
      }

      // Found the target player taking any action on the current street
      if (act.player === targetName && RANGE_UPDATE_ACTIONS.includes(act.action)) {
        targetIdx = i;
        break;
      }
    }

    // Street change comes first — pause at the start of the new street for range update
    if (streetChangeIdx >= 0) {
      const exposed = getExposedCards(hand, newStreet);
      const comboCounts = computeAllComboCounts(exposed);
      get()._lockRemovedCells();

      set({
        actionIndex: streetChangeIdx - 1,
        currentStreet: newStreet,
        comboCounts,
        phase: 'REPLAYING',
      });
      get()._saveSnapshot(`Start of ${newStreet}`);
      set({ phase: 'AWAITING_RANGE_UPDATE' });
      return;
    }

    // No street change — found a target action on the same street
    if (targetIdx >= 0) {
      const act = actions[targetIdx];
      const label = `${act.action} (${currentStreet})`;
      set({ actionIndex: targetIdx, phase: 'REPLAYING' });
      get()._saveSnapshot(label);
      set({ phase: 'AWAITING_RANGE_UPDATE' });
    } else {
      // No more target actions and no more streets — go to showdown
      get()._advanceToShowdown();
    }
  },

  nextStreet: () => {
    const { hand, actionIndex, currentStreet } = get();
    if (!hand) return;

    const streetOrder: Street[] = ['preflop', 'flop', 'turn', 'river'];
    const currentIdx = streetOrder.indexOf(currentStreet);
    if (currentIdx === -1 || currentIdx >= streetOrder.length - 1) {
      get()._advanceToShowdown();
      return;
    }

    const nextStr = streetOrder[currentIdx + 1];
    const actions = hand.actions ?? [];
    const nextActionIdx = actions.findIndex(
      (a, i) => i > actionIndex && parseStreet(a.street ?? '') === nextStr
    );

    const exposed = getExposedCards(hand, nextStr);
    const comboCounts = computeAllComboCounts(exposed);
    get()._lockRemovedCells();

    set({
      currentStreet: nextStr,
      comboCounts,
      actionIndex: nextActionIdx >= 0 ? nextActionIdx - 1 : actions.length - 1,
      phase: 'AWAITING_RANGE_UPDATE',
    });
    get()._saveSnapshot(`Jump to ${nextStr}`);
  },

  skipToShowdown: () => {
    const { hand } = get();
    if (!hand) return;
    get()._advanceToShowdown(true);
  },

  replayHand: () => {
    const { hand, matrixDefault } = get();
    if (!hand) return;
    const matrix = makeInitialMatrix(matrixDefault);
    const comboCounts = computeAllComboCounts([]);
    set({
      phase: 'AWAITING_RANGE_UPDATE',
      actionIndex: -1,
      currentStreet: 'preflop',
      matrixState: matrix,
      comboCounts,
      eliminationLog: [],
      snapshots: [],
      _matrixHistory: [],
      scoreBreakdown: null,
      sessionId: null,
      focusedCell: 0,
    });
    get()._saveSnapshot('Replay start');
  },

  nextHand: async () => {
    const { filtersUsed, matrixDefault } = get();
    if (!filtersUsed) return;
    await get().startSession(filtersUsed, matrixDefault);
  },

  continueAfterRangeUpdate: () => {
    const { phase, actionIndex, hand } = get();
    if (phase !== 'AWAITING_RANGE_UPDATE') return;
    const actions = hand?.actions ?? [];
    if (actionIndex >= actions.length - 1) {
      get()._advanceToShowdown();
    } else {
      // Advance to the next target player action
      set({ phase: 'REPLAYING' });
      get().nextAction();
    }
  },

  toggleCell: (idx: number) => {
    const { matrixState, eliminationLog, currentStreet, phase, _matrixHistory } = get();
    if (phase === 'SHOWDOWN') return;

    const current = matrixState[idx];
    if (current === 'ELIMINATED') return;

    set({ _matrixHistory: [..._matrixHistory, [...matrixState]] });

    const next = [...matrixState];
    if (current === 'HIGHLIGHTED') {
      next[idx] = 'REMOVED';
      const handLabel = MATRIX_CELLS[idx]?.category ?? `Cell ${idx}`;
      const entry: EliminationLogEntry = {
        handLabel,
        street: currentStreet,
        action: 'manual',
        timestamp: Date.now(),
      };
      set({ matrixState: next, eliminationLog: [...eliminationLog, entry] });
    } else if (current === 'REMOVED') {
      next[idx] = 'HIGHLIGHTED';
      const newLog = eliminationLog.filter(e => e.handLabel !== (MATRIX_CELLS[idx]?.category ?? ''));
      set({ matrixState: next, eliminationLog: newLog });
    } else {
      next[idx] = 'HIGHLIGHTED';
      set({ matrixState: next });
    }
  },

  _lockRemovedCells: () => {
    const { matrixState, eliminationLog, currentStreet } = get();
    const now = Date.now();
    const newLog = [...eliminationLog];
    const next = matrixState.map((s, idx) => {
      if (s === 'REMOVED') {
        const label = MATRIX_CELLS[idx]?.category ?? `Cell ${idx}`;
        if (!newLog.find(e => e.handLabel === label)) {
          newLog.push({ handLabel: label, street: currentStreet, action: 'locked', timestamp: now });
        }
        return 'ELIMINATED' as MatrixCellStatus;
      }
      return s;
    });
    set({ matrixState: next, eliminationLog: newLog });
  },

  selectAll: () => {
    const { matrixState } = get();
    const next = matrixState.map((s) => (s === 'ELIMINATED' ? 'ELIMINATED' : 'HIGHLIGHTED'));
    set({ matrixState: next as MatrixCellStatus[] });
  },

  clearAll: () => {
    const { matrixState } = get();
    const next = matrixState.map((s) => (s === 'ELIMINATED' ? s : 'UNHIGHLIGHTED'));
    set({ matrixState: next as MatrixCellStatus[] });
  },

  undoLastToggle: () => {
    const { _matrixHistory } = get();
    if (_matrixHistory.length === 0) return;
    const prev = _matrixHistory[_matrixHistory.length - 1];
    const restored = prev.map((s, i) => {
      const current = get().matrixState[i];
      if (current === 'ELIMINATED') return 'ELIMINATED' as MatrixCellStatus;
      return s;
    });
    set({
      matrixState: restored,
      _matrixHistory: _matrixHistory.slice(0, -1),
    });
  },

  setFocusedCell: (idx: number) => set({ focusedCell: idx }),

  _saveSnapshot: (triggerLabel: string) => {
    const { matrixState, currentStreet, snapshots } = get();
    const highlighted = matrixState
      .map((s, i) => s === 'HIGHLIGHTED' ? (MATRIX_CELLS[i]?.category ?? '') : '')
      .filter(Boolean);

    const snap: RangeSnapshot = {
      street: currentStreet,
      triggerLabel,
      timestamp: Date.now(),
      matrixState: [...matrixState],
      highlightedCategories: highlighted,
    };
    set({ snapshots: [...snapshots, snap] });
  },

  _advanceToShowdown: (skipped = false) => {
    const { hand, matrixState } = get();
    if (!hand) return;

    const actions = hand.actions ?? [];
    const lastAction = actions.length > 0 ? actions[actions.length - 1] : null;
    const finalStreet = lastAction?.street ? parseStreet(lastAction.street) : get().currentStreet;
    const exposed = getExposedCards(hand, 'river');
    const comboCounts = computeAllComboCounts(exposed);

    set({
      actionIndex: actions.length - 1,
      currentStreet: finalStreet,
      comboCounts,
    });

    get()._saveSnapshot('Showdown');

    const finalMatrix = get().matrixState;
    const highlighted = finalMatrix
      .map((s, i) => s === 'HIGHLIGHTED' ? (MATRIX_CELLS[i]?.category ?? '') : null)
      .filter(Boolean) as string[];

    const winnerHoleCards = hand.winner?.holeCards ?? '';
    const winnerCategory = holeCardsToCategory(winnerHoleCards) ?? winnerHoleCards as HandCategory;

    const allSnapshots = get().snapshots;
    const isolationStreet = detectIsolationStreet(winnerCategory as HandCategory, allSnapshots);

    const breakdown = computeScore({
      actualHand: winnerCategory as HandCategory,
      finalRange: highlighted as HandCategory[],
      eliminationLog: get().eliminationLog.map(e => ({
        handLabel: e.handLabel as HandCategory,
        street: e.street,
        timestamp: e.timestamp,
      })),
      isolationStreet,
    });

    set({
      phase: 'SHOWDOWN',
      scoreBreakdown: breakdown,
    });

    // Persist session asynchronously
    const state = get();
    const sessionPayload = {
      handId: hand.id,
      targetSeat: hand.winner?.seat ?? 0,
      anonymized: false,
      filtersJson: JSON.stringify(state.filtersUsed),
      snapshots: allSnapshots,
      eliminationLog: state.eliminationLog,
      finalRange: highlighted,
      actualHand: winnerCategory,
      isolationStreet: breakdown.isolationStreet,
      hit: breakdown.hit,
      nearMiss: breakdown.hit ? null : breakdown.nearMissCredit > 0,
      nearMissDistance: breakdown.nearMissDistance,
      baseScore: breakdown.baseScore,
      nearMissCredit: breakdown.nearMissCredit,
      streetBonus: breakdown.streetBonus,
      accuracyScore: breakdown.totalScore,
      completed: !skipped,
      note: null,
      matrixDefault: state.matrixDefault,
    };

    apiPost<{ id: string }>('/api/sessions', sessionPayload)
      .then(res => { if (res?.id) set({ sessionId: res.id }); })
      .catch((e) => { console.error(e); showErrorToast('Failed to save session results.'); });
  },
}));
